import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Eye, Heart, BadgeCheck, MessageCircle, Share2, Pencil, Package, MessageSquare, CheckCircle2, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import AnimatedPrice from "@/components/AnimatedPrice";
import CommodityIcon from "@/components/CommodityIcon";
import CommentsSheet from "@/components/CommentsSheet";
import ConfirmDialog from "@/components/ConfirmDialog";
import ImageGallery from "@/components/ImageGallery";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { format, currency } = useCurrency();
  const [showComments, setShowComments] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const deletedRef = useRef(false);

  const { data: cData } = useQuery({
    queryKey: ["comments", id],
    queryFn: async () => (await api.get(`/listings/${id}/comments`)).data,
  });
  const cCount = cData?.count ?? 0;

  const { data, isLoading } = useQuery({
    queryKey: ["listing", id],
    queryFn: async () => (await api.get(`/listings/${id}`)).data.listing,
  });

  if (isLoading || !data) {
    return <div className="p-4"><div className="skeleton mb-4 h-72 w-full" /><div className="skeleton mb-2 h-6 w-2/3" /><div className="skeleton h-4 w-1/2" /></div>;
  }

  const l = data;
  const mine = l.seller?.id === user.id;
  const showOriginal = l.currency && l.currency !== currency;

  const toggleFav = async () => {
    try {
      const { data: r } = await api.post(`/favorites/${l.id}`);
      qc.setQueryData(["listing", id], (old) => ({ ...old, is_favorite: r.favorited }));
      toast.success(r.favorited ? "Saved" : "Removed");
    } catch { toast.error("Please log in"); }
  };

  const startChat = async () => {
    try {
      const { data: r } = await api.post("/conversations", { other_user_id: l.seller.id, listing_id: l.id });
      nav(`/messages/${r.conversation_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start chat"); }
  };

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: l.title, url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Link copied"); }
  };

  const toggleSold = async () => {
    const next = l.status === "sold" ? "available" : "sold";
    try {
      await api.post(`/listings/${l.id}/status?status=${next}`);
      qc.setQueryData(["listing", id], (old) => ({ ...old, status: next }));
      toast.success(next === "sold" ? "Marked as sold — hidden from the marketplace" : "Marked available");
    } catch { toast.error("Failed to update"); }
  };

  const doDelete = async () => {
    await api.delete(`/listings/${l.id}`);
    qc.invalidateQueries({ queryKey: ["home-listings"] });
    deletedRef.current = true;
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {/* Gallery */}
      <ImageGallery images={l.images || []} alt={l.title}>
        <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between p-4">
          <button onClick={() => nav(-1)} data-testid="detail-back" className="glass pressable flex h-10 w-10 items-center justify-center rounded-full"><ArrowLeft size={18} color="#fff" /></button>
          <div className="flex gap-2">
            <button onClick={share} data-testid="detail-share" className="glass pressable flex h-10 w-10 items-center justify-center rounded-full"><Share2 size={17} color="#fff" /></button>
            <button onClick={toggleFav} data-testid="detail-fav" className="glass pressable flex h-10 w-10 items-center justify-center rounded-full">
              <Heart size={18} fill={l.is_favorite ? "#F59E0B" : "transparent"} color={l.is_favorite ? "#F59E0B" : "#fff"} />
            </button>
          </div>
        </div>
        <span className="absolute bottom-4 left-4 rounded-full px-3 py-1 text-xs font-bold text-white"
              style={{ background: l.status === "sold" ? "var(--red)" : "var(--green)" }}>
          {l.status === "sold" ? "SOLD" : "AVAILABLE"}
        </span>
      </ImageGallery>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-2xl font-extrabold leading-tight">{l.title}</h1>
          <div className="text-right">
            <AnimatedPrice amount={l.price} from={l.currency || "USD"} className="font-display block text-2xl font-extrabold accent" />
            {showOriginal && <p className="text-xs text-dim">{format(l.price, l.currency, { from: l.currency })} original</p>}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Pill><CommodityIcon name={l.commodity} size={13} rounded={8} /> {l.commodity}</Pill>
          {l.quantity && <Pill><Package size={14} className="accent" /> {l.quantity}</Pill>}
          {l.location && <Pill><MapPin size={14} className="accent" /> {l.location}</Pill>}
          <Pill><Eye size={14} className="accent" /> {l.views} views</Pill>
        </div>

        {l.description && (
          <div className="goe-card mt-4 p-4">
            <p className="mb-1 text-xs font-bold uppercase text-dim">Description</p>
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{l.description}</p>
          </div>
        )}

        {/* Seller */}
        {l.seller && (
          <button onClick={() => nav(`/u/${l.seller.id}`)} data-testid="detail-seller"
                  className="goe-card lift pressable mt-4 flex w-full items-center gap-3 p-4 text-left">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-accent text-lg font-bold text-white">
              {l.seller.avatar ? <img src={fileUrl(l.seller.avatar)} alt="" className="h-full w-full object-cover" /> : (l.seller.name?.[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="font-display font-bold">{l.seller.name}</p>
                {l.seller.verified && <BadgeCheck size={15} className="accent" />}
              </div>
              <p className="text-xs text-dim">{(l.seller.roles || []).join(" · ")}</p>
            </div>
            <span className="text-xs font-bold accent">View →</span>
          </button>
        )}
        {/* Comments entry */}
        <button onClick={() => setShowComments(true)} data-testid="open-comments"
                className="goe-card lift pressable mt-4 flex w-full items-center gap-3 p-4 text-left">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: "rgba(245,158,11,0.12)" }}>
            <MessageSquare size={20} className="accent" />
          </div>
          <div className="flex-1">
            <p className="font-display font-bold">Comments</p>
            <p className="text-xs text-dim">{cCount} comment{cCount === 1 ? "" : "s"} · join the conversation</p>
          </div>
          <span className="text-sm font-bold accent">View →</span>
        </button>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-1/2 z-40 w-full -translate-x-1/2 glass px-4 py-3" style={{ maxWidth: 520 }}>
        {mine ? (
          <div className="flex gap-2">
            <button onClick={toggleSold} data-testid="detail-sold"
                    className="pressable flex flex-1 items-center justify-center gap-2 rounded-2xl py-3.5 font-bold"
                    style={{ background: "var(--surface-2)", color: l.status === "sold" ? "var(--green)" : "var(--text)" }}>
              <CheckCircle2 size={18} /> {l.status === "sold" ? "Sold" : "Mark Sold"}
            </button>
            <button onClick={() => nav(`/post/${l.id}`)} data-testid="detail-edit"
                    className="btn-accent pressable flex flex-[1.3] items-center justify-center gap-2 py-3.5 font-extrabold">
              <Pencil size={18} /> Edit
            </button>
            <button onClick={() => setConfirmDel(true)} data-testid="detail-delete"
                    className="pressable flex h-[52px] w-[52px] items-center justify-center rounded-2xl"
                    style={{ background: "rgba(255,92,92,0.15)", color: "var(--red)" }}>
              <Trash2 size={19} />
            </button>
          </div>
        ) : (
          <button onClick={startChat} data-testid="detail-contact" className="btn-accent flex w-full items-center justify-center gap-2 py-3.5 font-extrabold">
            <MessageCircle size={19} /> Contact Seller
          </button>
        )}
      </div>
      <div style={{ height: 80 }} />

      <CommentsSheet listingId={id} open={showComments} onClose={() => setShowComments(false)} />
      <ConfirmDialog open={confirmDel} title="Delete listing?"
                     description="This permanently removes the listing and its comments. This cannot be undone."
                     confirmLabel="Delete" onConfirm={doDelete}
                     onClose={() => { setConfirmDel(false); if (deletedRef.current) nav(-1); }} />
    </motion.div>
  );
}

const Pill = ({ children }) => (
  <span className="glass flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold">{children}</span>
);
